// Graded (certified) Palworld card price layer.
//
// Raw singles are priced on /prices and in the index. Slabs are a different
// market: a certified copy is scarce at the top grade and trades at a multiple
// of raw, so it gets its own page per chase printing at /graded-card-prices/<code>.
// Nothing here feeds the raw market price or the base-1,000 index.
import { CHASE_CARDS } from "./guides-chase";
import { OFFICIAL_CARD_BY_CODE, normalizeCardCode, type OfficialCard } from "./tcg-cards";

/** Chase printings that get their own graded price page (real scans only). */
export const GRADED_CARDS: OfficialCard[] = CHASE_CARDS.filter((c) => !!c.image);

const BY_CODE = new Map(GRADED_CARDS.map((c) => [c.code.toUpperCase(), c]));

export function gradedPath(code: string): string {
  return `/graded-card-prices/${code.toUpperCase()}`;
}

/** Resolves any typed spelling to a tracked graded page card, or undefined. */
export function resolveGraded(raw: string): OfficialCard | undefined {
  const code = normalizeCardCode(raw).toUpperCase();
  return BY_CODE.get(code) ?? BY_CODE.get((OFFICIAL_CARD_BY_CODE.get(code)?.code ?? "").toUpperCase());
}

/**
 * Typical PSA 10 premium over raw, by printed rarity. These are ranges observed
 * across modern TCG releases and are labelled as typical ranges on the page —
 * the live certified asking price shown next to them is the real number.
 */
export const GRADED_MULTIPLE: Record<string, string> = {
  OSR: "roughly 4x to 10x raw",
  SSP: "roughly 3x to 8x raw",
  SSS: "roughly 3x to 7x raw",
  TSP: "roughly 3x to 6x raw",
};

export function gradedFaq(c: OfficialCard) {
  const mult = GRADED_MULTIPLE[c.rarity] ?? "a multiple of raw";
  return [
    {
      q: `How much is a PSA 10 ${c.name} ${c.code} worth?`,
      a: `A PSA 10 ${c.code} ${c.name} trades at ${mult}. The certified asking range on this page is read from live marketplace listings for slabbed copies only and is shown next to the raw market price, so you can see the premium as a multiple before you buy or submit.`,
    },
    {
      q: `Is ${c.code} ${c.name} worth grading?`,
      a: `Yes, when the copy is genuinely gem-mint: ${c.code} is a ${c.rarity} chase printing, so the gap between a PSA 10 and a raw copy usually clears grading and shipping costs comfortably. A copy with soft corners or off-centre printing will grade 8 or 9 and often returns less than the fee.`,
    },
    {
      q: `What is the difference between BGS 9.5 and PSA 10 for ${c.code}?`,
      a: `A BGS 9.5 Gem Mint and a PSA 10 sit at a similar level for ${c.code}, while a BGS Black Label 10 sits well above both because all four subgrades must be perfect. PSA 10 slabs generally resell fastest because buyer recognition is broadest.`,
    },
    {
      q: `How many copies of ${c.code} have been graded?`,
      a: `Check the population report for the exact printed code ${c.code} at PSA, Beckett and CGC. The count at the top grade is the scarcity half of the price: a small PSA 10 population with steady demand is what keeps a premium from collapsing.`,
    },
    {
      q: `Do graded ${c.name} sales change the Palworld price index?`,
      a: `No. Certified quotes for ${c.code} are tracked as a separate series and never blend into the raw market price, the raw low/high band or the Pal Stars TCG Price Index.`,
    },
  ];
}
